/**
 * A chave do telefone no SPA (chamado de 21/10/2026, P8) — a regra de casamento, e não de exibição.
 *
 * **`chaveDoTelefone` é cópia de `chave_do_telefone`, de `crm-backend/compartilhado/telefone.py`**,
 * e o cabeçalho de lá explica a regra e por que cópia divergente é perigosa. Esta não reabre o
 * risco porque está presa ao original por dois lados (`chave-do-telefone.test.ts`): os vetores de
 * `compartilhado/tests/test_telefone.py` espelhados um a um, e a leitura `?raw` da fonte Python,
 * com as constantes afirmadas e uma impressão do corpo da função — qualquer mudança lá reprova a
 * suíte daqui.
 *
 * **Ela mora em `src/telefone/` desde a 13.2**, e não mais no `comunicacao`: a máscara de telefone
 * (`mascara-do-telefone.ts`, ao lado) usa os mesmos `DIGITOS` e os mesmos tamanhos, e o próprio
 * cabeçalho antigo mandava mudar de lugar em vez de duplicar. A busca da lista de conversas
 * (`prepararTermo`, `numeroCasa`) continua no `comunicacao`, em `busca-por-telefone.ts`, importando
 * daqui.
 *
 * **Só dígitos ASCII, e o filtro é a lista `DIGITOS`, como na Python.** Em JS `\d` já é só ASCII,
 * mesmo com a flag `/u`; a lista deixa a intenção escrita. E `\p{Nd}` **não** seria fiel: ele
 * aceita o dígito arábico-índico e o de largura plena, que a Python descarta de propósito — o
 * mesmo número em duas grafias daria duas chaves.
 */

/** O DDI do Brasil — `DDI_DO_BRASIL` da Python. */
export const DDI_DO_BRASIL = '55'
/** DDD mais oito dígitos — `TAMANHO_BRASILEIRO_SEM_O_NONO`. */
export const TAMANHO_BRASILEIRO_SEM_O_NONO = 10
/** DDD mais nove dígitos — `TAMANHO_BRASILEIRO_COM_O_NONO`. */
export const TAMANHO_BRASILEIRO_COM_O_NONO = 11
/** A posição do nono dígito no número sem DDI — `_INDICE_DO_NONO`. */
export const INDICE_DO_NONO = 2
/** Os dez algarismos ASCII, e só eles — `DIGITOS`. */
export const DIGITOS = '0123456789'

/** Só os dígitos ASCII do texto, na ordem. */
export function digitosDe(texto: string): string {
  let digitos = ''
  for (const caractere of texto) {
    if (DIGITOS.includes(caractere)) {
      digitos += caractere
    }
  }
  return digitos
}

/**
 * A chave comparável daquele telefone, ou `null` quando não há dígito nenhum — `chave_do_telefone`.
 *
 * Duas grafias do mesmo número brasileiro produzem a mesma chave: com e sem DDI, com e sem o nono,
 * com e sem máscara. O resto sai como os dígitos que entraram.
 */
export function chaveDoTelefone(texto: string | null | undefined): string | null {
  if (texto === null || texto === undefined) {
    return null
  }
  let digitos = digitosDe(texto)
  if (digitos === '') {
    return null
  }

  if (digitos.startsWith(DDI_DO_BRASIL)) {
    const semDdi = digitos.slice(DDI_DO_BRASIL.length)
    if (
      semDdi.length === TAMANHO_BRASILEIRO_SEM_O_NONO ||
      semDdi.length === TAMANHO_BRASILEIRO_COM_O_NONO
    ) {
      digitos = semDdi
    }
  }

  if (digitos.length === TAMANHO_BRASILEIRO_COM_O_NONO && digitos[INDICE_DO_NONO] === '9') {
    digitos = digitos.slice(0, INDICE_DO_NONO) + digitos.slice(INDICE_DO_NONO + 1)
  }

  return digitos
}
